import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Modal,
  Badge,
  Spinner,
  Tab,
  Nav,
  Tabs
} from 'react-bootstrap';
import {
  PersonPlus,
  Pencil,
  Trash,
  Eye,
  Key,
  Send
} from 'react-bootstrap-icons';

const API_BASE_URL = 'http://localhost:5000/api'; // Update with your backend URL

const AddEmployee = () => {
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNo: '',
    password: '',
    role: 'employee',
    isActive: true
  });

  // UI states
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [currentEmployee, setCurrentEmployee] = useState(null);
  
  // Reset Password states
  const [resetPasswordData, setResetPasswordData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // OTP states
  const [otpData, setOtpData] = useState({
    phoneNo: '',
    email: ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (selectedEmployee) {
        // Update employee
        const response = await axios.put(`${API_BASE_URL}/employees/update`, {
          _id: selectedEmployee._id,
          ...formData
        });
        showAlert('Employee updated successfully!', 'success');
      } else {
        // Create new employee
        const response = await axios.post(`${API_BASE_URL}/employees/create`, formData);
        showAlert('Employee created successfully!', 'success');
      }
      
      resetForm();
    } catch (error) {
      showAlert(error.response?.data?.message || 'Error saving employee', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phoneNo: '',
      password: '',
      role: 'employee',
      isActive: true
    });
    setSelectedEmployee(null);
  };

  const showAlert = (message, variant) => {
    setAlert({ show: true, message, variant });
    setTimeout(() => {
      setAlert({ show: false, message: '', variant: 'success' });
    }, 5000);
  };

  // Open modal for actions
  const openModal = (type, employee = null) => {
    setModalType(type);
    setCurrentEmployee(employee);
    setShowModal(true);
    
    if (type === 'resetPassword' && employee) {
      setResetPasswordData(prev => ({ ...prev, email: employee.email }));
    }
    if (type === 'sendOtp' && employee) {
      setOtpData(prev => ({ ...prev, phoneNo: employee.phoneNo }));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentEmployee(null);
    setResetPasswordData({
      email: '',
      otp: '',
      newPassword: '',
      confirmPassword: ''
    });
    setOtpData({
      phoneNo: '',
      email: ''
    });
  };

  // Handle delete employee
  const handleDelete = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/employees/delete`, {
        data: { _id: currentEmployee._id }
      });
      showAlert('Employee deleted successfully!', 'success');
      resetForm();
      closeModal();
    } catch (error) {
      showAlert(error.response?.data?.message || 'Error deleting employee', 'danger');
    }
  };

  // Send OTP
  const handleSendOtp = async () => {
    try {
      await axios.post(`${API_BASE_URL}/employees/send-otp`, {
        phoneNo: otpData.phoneNo
      });
      showAlert('OTP sent successfully!', 'success');
      closeModal();
    } catch (error) {
      showAlert(error.response?.data?.message || 'Error sending OTP', 'danger');
    }
  };

  // Send reset password OTP
  const handleSendResetOtp = async () => {
    try {
      await axios.post(`${API_BASE_URL}/employees/send-reset-otp`, {
        email: resetPasswordData.email
      });
      showAlert('Reset OTP sent to email!', 'success');
    } catch (error) {
      showAlert(error.response?.data?.message || 'Error sending reset OTP', 'danger');
    }
  };

  // Reset password with OTP
  const handleResetPassword = async () => {
    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      showAlert('Passwords do not match!', 'danger');
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/employees/change-password-otp`, {
        email: resetPasswordData.email,
        otp: resetPasswordData.otp,
        newPassword: resetPasswordData.newPassword
      });
      showAlert('Password reset successfully!', 'success');
      closeModal();
    } catch (error) {
      showAlert(error.response?.data?.message || 'Error resetting password', 'danger');
    }
  };

  return (
    <Container fluid className="py-4">
      {/* Alert */}
      {alert.show && (
        <Alert variant={alert.variant} onClose={() => setAlert({ show: false, message: '', variant: 'success' })} dismissible>
          {alert.message}
        </Alert>
      )}

      <Row>
        <Col>
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <PersonPlus className="me-2" />
                {selectedEmployee ? "Edit Employee" : "Add New Employee"}
              </h5>
              {selectedEmployee && (
                <Button 
                  variant="light" 
                  size="sm"
                  onClick={() => {
                    resetForm();
                  }}
                >
                  <PersonPlus className="me-1" /> Add New Employee
                </Button>
              )}
            </Card.Header>
            
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Full Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter full name"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address *</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter email address"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone Number *</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phoneNo"
                        value={formData.phoneNo}
                        onChange={handleInputChange}
                        placeholder="Enter phone number"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Password {!selectedEmployee && '*'}</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder={selectedEmployee ? "Leave blank to keep current" : "Enter password"}
                        required={!selectedEmployee}
                        minLength="6"
                      />
                      {selectedEmployee && (
                        <Form.Text className="text-muted">
                          Leave blank to keep current password
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Role *</Form.Label>
                      <Form.Select
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="employee">Employee</option>
                        <option value="inspector">Inspector</option>
                        <option value="admin">Admin</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3 d-flex align-items-center">
                      <Form.Check
                        type="checkbox"
                        name="isActive"
                        id="isActive"
                        label="Active Employee"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="mt-3"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end gap-2">
                  {selectedEmployee && (
                    <>
                      <Button
                        variant="outline-info"
                        onClick={() => openModal('view', selectedEmployee)}
                      >
                        <Eye className="me-1" />
                        View Details
                      </Button>
                      <Button
                        variant="outline-warning"
                        onClick={() => openModal('sendOtp', selectedEmployee)}
                      >
                        <Send className="me-1" />
                        Send OTP
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => openModal('resetPassword', selectedEmployee)}
                      >
                        <Key className="me-1" />
                        Reset Password
                      </Button>
                      <Button
                        variant="outline-danger"
                        onClick={() => openModal('delete', selectedEmployee)}
                      >
                        <Trash className="me-1" />
                        Delete
                      </Button>
                    </>
                  )}
                  <Button
                    variant="secondary"
                    onClick={resetForm}
                  >
                    Reset
                  </Button>
                  <Button
                    variant={selectedEmployee ? "warning" : "primary"}
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : selectedEmployee ? (
                      <>
                        <Pencil className="me-1" />
                        Update Employee
                      </>
                    ) : (
                      <>
                        <PersonPlus className="me-1" />
                        Create Employee
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modals */}
      {/* View Employee Modal */}
      <Modal show={showModal && modalType === 'view'} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <Eye className="me-2" />
            Employee Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentEmployee && (
            <Row>
              <Col md={6}>
                <h6>Personal Information</h6>
                <p><strong>Name:</strong> {currentEmployee.name}</p>
                <p><strong>Email:</strong> {currentEmployee.email}</p>
                <p><strong>Phone:</strong> {currentEmployee.phoneNo}</p>
                <p><strong>Role:</strong> <Badge bg="info">{currentEmployee.role}</Badge></p>
              </Col>
              <Col md={6}>
                <h6>Account Status</h6>
                <p>
                  <strong>Status:</strong>{' '}
                  {currentEmployee.isActive !== false ? (
                    <Badge bg="success">Active</Badge>
                  ) : (
                    <Badge bg="secondary">Inactive</Badge>
                  )}
                </p>
                <p><strong>Created:</strong> {new Date(currentEmployee.createdAt).toLocaleDateString()}</p>
                <p><strong>Last Updated:</strong> {new Date(currentEmployee.updatedAt).toLocaleDateString()}</p>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showModal && modalType === 'delete'} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <Trash className="me-2" />
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete employee <strong>{currentEmployee?.name}</strong>?
          <br />
          <small className="text-danger">This action cannot be undone.</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete Employee
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Send OTP Modal */}
      <Modal show={showModal && modalType === 'sendOtp'} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            <Send className="me-2" />
            Send OTP to Employee
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Send OTP to <strong>{currentEmployee?.name}</strong> ({currentEmployee?.phoneNo})?
          </p>
          <p className="text-muted small">
            OTP will be sent to the registered email: {currentEmployee?.email}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSendOtp}>
            Send OTP
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reset Password Modal */}
      <Modal show={showModal && modalType === 'resetPassword'} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            <Key className="me-2" />
            Reset Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Employee Email</Form.Label>
              <Form.Control
                type="email"
                value={resetPasswordData.email}
                onChange={(e) => setResetPasswordData(prev => ({ ...prev, email: e.target.value }))}
                readOnly={!!currentEmployee}
              />
            </Form.Group>

            <div className="d-grid mb-3">
              <Button
                variant="outline-primary"
                onClick={handleSendResetOtp}
              >
                Send OTP to Email
              </Button>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>OTP</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter OTP received via email"
                value={resetPasswordData.otp}
                onChange={(e) => setResetPasswordData(prev => ({ ...prev, otp: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter new password"
                value={resetPasswordData.newPassword}
                onChange={(e) => setResetPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Confirm new password"
                value={resetPasswordData.confirmPassword}
                onChange={(e) => setResetPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleResetPassword}>
            Reset Password
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AddEmployee;